import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Email-only signup — first step of the mobile sign-in funnel. POSTing an
 * email issues a 6-digit one-time code via SES, returned to the client as
 * `{ status: 'code_sent', email }`. The session is NOT minted here — that
 * happens on /v1/auth/email-verify-code once the user types the code back
 * in. Both new and returning users go through the same gate so the
 * "registered? unregistered?" response shape can't leak the user list.
 */
export const EmailSignupRequest = z.object({
  email: z.string().email().max(254),
  /** Free-form marker so we can split funnel reporting (e.g., 'mobile_ios'). */
  source: z.string().max(64).optional(),
});
export type EmailSignupRequest = z.infer<typeof EmailSignupRequest>;

export const EmailSignupResponse = z.object({
  status: z.literal('code_sent'),
  /**
   * Echo of the normalized email the code was sent to. The client uses
   * this on the verify screen so the user sees the destination they're
   * confirming (and to detect a typo before they wait for an email that
   * never arrives).
   */
  email: z.string().email(),
  /**
   * Seconds before the code expires. The verify screen surfaces a
   * "code expires in N min" hint + auto-disables the input at expiry.
   */
  expiresIn: z.number().int().positive(),
  /**
   * Seconds the client must wait before requesting a new code for this
   * email. Lets the resend button render its own countdown without
   * polling the server.
   */
  resendAvailableInSec: z.number().int().nonnegative().optional(),
});
export type EmailSignupResponse = z.infer<typeof EmailSignupResponse>;

/**
 * Email-OTP verification — second step of the signup funnel. The client
 * posts the 6-digit code received via SES; the server hash-compares it
 * against the stored row (5-attempt cap, 10-min TTL) and on success
 * creates the user row if new, then mints the access + refresh session
 * the mobile app caches. Same response shape as the prior pre-OTP
 * signup endpoint had — clients that already drove a session off that
 * shape need only swap which endpoint they call.
 */
export const EmailVerifyCodeRequest = z.object({
  email: z.string().email().max(254),
  code: z.string().regex(/^\d{6}$/, 'Code must be 6 digits'),
  /**
   * Runtime platform the mobile client is on. Server uses this to stamp
   * the initial device row it creates in `mintMobileAccessToken`. Without
   * this, the server historically hardcoded `platform: 'ios'`, silently
   * misrouting every Android install's push through APNS until the
   * client's follow-up `registerMobilePushToken` upsert corrected it.
   * Optional — legacy clients that don't send it fall back to `'ios'`.
   */
  platform: z.enum(['ios', 'android']).optional(),
});
export type EmailVerifyCodeRequest = z.infer<typeof EmailVerifyCodeRequest>;

export const EmailVerifyCodeResponse = z.object({
  status: z.enum(['created', 'existing']),
  userId: UserId,
  /** Short-lived access token (~24h). Same shape as social-auth/refresh. */
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  /** Long-lived (30d) refresh token. Mobile persists this for background refresh. */
  refreshToken: z.string().optional(),
  /** DeviceId the server registered for this verified session. */
  deviceId: z.string().optional(),
  /** Whether the user has completed the first-run setup wizard. */
  hasSeenWizard: z.boolean().optional(),
});
export type EmailVerifyCodeResponse = z.infer<typeof EmailVerifyCodeResponse>;

/**
 * Social-login providers offered on the mobile sign-in screen. Apple is
 * required by App Store policy whenever any third-party social login
 * appears, so it sits first. Google + Facebook + Amazon round out the
 * mainstream identity set.
 */
export const SocialAuthProvider = z.enum(['apple', 'google', 'facebook', 'amazon']);
export type SocialAuthProvider = z.infer<typeof SocialAuthProvider>;

/**
 * Social-auth contract. The mobile app posts the provider's identity
 * token (Apple/Google: id_token JWT) or access_token (Facebook/Amazon —
 * server-exchanged for the user's profile email). The server verifies,
 * extracts the email claim, upserts a User row, and returns the same
 * `{status, userId, email}` shape as the email-signup endpoint.
 *
 * Mobile is the only client. The desktop credentials itself solely
 * through the 6-digit pair-redeem flow; an earlier iteration carried a
 * `deviceMetadata` field for a desktop OAuth branch, which has been
 * removed.
 */
export const SocialAuthRequest = z.object({
  provider: SocialAuthProvider,
  /**
   * Provider-issued identity token:
   *  - Apple: id_token JWT
   *  - Google: id_token JWT
   *  - Facebook: access_token (exchanged at https://graph.facebook.com/me)
   *  - Amazon: access_token (exchanged at https://api.amazon.com/user/profile)
   */
  identityToken: z.string().min(1),
  /**
   * Optional email override sent by clients that surface it during the
   * native consent sheet (Apple). Apple only returns the email on first
   * authorisation, so callers must persist + resend it on subsequent
   * requests.
   */
  email: z.string().email().optional(),
  /**
   * Runtime platform of the mobile client. Same rationale as
   * EmailVerifyCodeRequest.platform — server uses it to stamp the initial
   * device row correctly. Optional for backward-compat with older clients
   * (server falls back to `'ios'`).
   */
  platform: z.enum(['ios', 'android']).optional(),
});
export type SocialAuthRequest = z.infer<typeof SocialAuthRequest>;

export const SocialAuthResponse = z.object({
  status: z.enum(['created', 'existing']),
  userId: UserId,
  email: z.string().email(),
  /**
   * Short-lived access token (~24h) the client should send as
   * `Authorization: Bearer <token>` on every authenticated API call.
   * Same shape as the email-signup response.
   */
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  /**
   * Long-lived (30d) refresh token. The mobile client persists this and
   * exchanges it via `/v1/desktop/refresh` to mint fresh access tokens
   * before expiry — without this loop the access token would silently
   * die every 24h and the user would be bounced to sign-in.
   *
   * Optional in the schema so older server builds without refresh-grant
   * minting are still type-compatible (the client falls back to a
   * forced re-sign-in flow in that case).
   */
  refreshToken: z.string().optional(),
  /**
   * Device row id the server registered for this session. Mobile
   * persists it so device-level calls (heartbeat / activate / revoke)
   * target the right row. Optional for the same back-compat reason as
   * `refreshToken`.
   */
  deviceId: z.string().optional(),
  /** Whether the user has completed the first-run setup wizard. */
  hasSeenWizard: z.boolean().optional(),
});
export type SocialAuthResponse = z.infer<typeof SocialAuthResponse>;

/**
 * Exact literal the client must echo back to confirm a self-service account
 * delete. Required by Apple App Store §5.1.1(v) — the in-app delete flow
 * must require an explicit second confirmation at submit time. We pin the
 * value to a fixed string (rather than a boolean) so a default-filled or
 * silently-coerced request body cannot accidentally trigger a delete.
 */
export const DELETE_ACCOUNT_CONFIRMATION = 'permanently_delete' as const;

/**
 * Body of `DELETE /v1/users/me`. The `confirmation` field is the wire-level
 * second-confirm — the mobile + desktop UIs already prompt the user once;
 * this is the belt-and-braces re-check the API layer enforces so an
 * authenticated-but-coerced client cannot delete a user's data with a
 * single-shot request.
 */
export const DeleteAccountRequest = z.object({
  confirmation: z.literal(DELETE_ACCOUNT_CONFIRMATION),
});
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequest>;

/**
 * Response of `DELETE /v1/users/me`. `tombstoneRetainedUntil` lets the
 * client surface the 7-year CCPA/GDPR retention window in the post-delete
 * "your account is gone" screen. Both timestamps are ISO 8601 UTC.
 */
export const DeleteAccountResponse = z.object({
  status: z.literal('deleted'),
  userId: UserId,
  deletedAt: IsoTimestamp,
  tombstoneRetainedUntil: IsoTimestamp,
});
export type DeleteAccountResponse = z.infer<typeof DeleteAccountResponse>;
